<template>
  <footer :class="{ footer: true }">
    <div class="footer-content">
      <div class="logo">
        <router-link to="/">
          <acv-auctions-primary-light-svg
            class="acv-logo"
            alt="The logo of ACV Auctions"
            title="ACV Auctions"
          />
        </router-link>
      </div>
      <div class="tw-mt-2 tw-md:tw-mt-0 links-row">
        <span
          v-for="(footerLink, index) in filteredUrls"
          :key="`footer-${index}`"
        >
          <a
            class="body-2 footer-link"
            :class="{
              'tw-whitespace-nowrap': index === filteredUrls.length - 1,
            }"
            :href="links.marketingSite[footerLink.url].url"
            :title="`${footerLink.name}. This opens in a new window.`"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="footerLink.name"
            @click="trackFooterLinkClicked(footerLink.analyticsEvent)"
          >
            {{ footerLink.name }}
          </a>
          <span
            v-if="index < filteredUrls.length - 1"
            class="footer-bullet"
          >
            &#32;&bull;&#32;
          </span>
        </span>
      </div>
      <div class="body-2 tw-text-black-370">
        <div
          :class="[
            'tw-flexbox-row-align-start-justify-between',
            'tw-w-full',
            'social-media-links',
          ]"
        >
          <span
            v-for="(socialLink, index) in socialsToInclude"
            :key="`social-${index}`"
          >
            <a
              class="social-media"
              :class="{
                'tw-whitespace-nowrap': index === socialsToInclude.length - 1,
              }"
              :href="socialLink.url"
              :title="`${socialLink.title}. This opens in a new window.`"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="socialLink.name"
              @click="trackSocialLinkClicked(socialLink.analyticsEvent)"
            >
              <component
                :is="`${socialLink.name}Icon`"
                :alt="`${socialLink.name} Icon`"
                :title="`${socialLink.title}. This opens in a new window.`"
              />
            </a>
          </span>
        </div>
      </div>
      <div class="body-2 tw-text-black-370">
        <div
          :class="[
            'tw-flexbox-row-align-start-justify-between',
            'tw-w-full',
            'app-store-badges',
          ]"
        >
          <div
            :class="[
              'app-store',
              'tw-flexbox-col',
            ]"
          >
            <a
              class="apple-app-store"
              target="_blank"
              rel="noopener noreferrer"
              :href="links.appStore.iOS"
              @click="trackAppStoreBadgeClicked()"
            >
              <span class="sr-only">Download on the App Store. This opens in a new window.</span>
              <app-store-button
                alt="App Store badge"
                title="Download on the App Store. This opens in a new window."
              />
            </a>
          </div>
          <div
            :class="[
              'app-store',
              'tw-flexbox-col',
            ]"
          >
            <a
              class="google-play-store"
              target="_blank"
              rel="noopener noreferrer"
              :href="links.appStore.android"
              @click="trackGooglePlayBadgeClicked()"
            >
              <span class="sr-only">Get it on Google Play. This opens in a new window.</span>
              <google-play-button
                alt="Google Play badge"
                title="Get it on Google Play. This opens in a new window."
              />
            </a>
          </div>
        </div>
      </div>
      <div class="body-2 tw-text-black-370 acv-copyright">
        &#xa9; {{ getCopyrightYear }} ACV Auctions, Inc. All Rights Reserved.
      </div>
    </div>
  </footer>
</template>

<script>
import { AcvAuctionsPrimaryLightSvg } from '@acv/shared-vuejs';
import Analytics from '@/mixins/Analytics';
import AppStoreButton from '@/assets/svg/app-store-button.svg';
import FacebookIcon from '@/assets/svg/facebook.svg';
import GooglePlayButton from '@/assets/svg/google-play-button.svg';
import InstagramIcon from '@/assets/svg/instagram.svg';
import LinkedInIcon from '@/assets/svg/linkedin.svg';
import TwitterIcon from '@/assets/svg/twitter.svg';
import links from '@/constants/links';
import FeatureFlags from '@/mixins/FeatureFlags';

export default {
  name: 'AcvFooter',
  components: {
    AcvAuctionsPrimaryLightSvg,
    AppStoreButton,
    FacebookIcon,
    GooglePlayButton,
    InstagramIcon,
    LinkedInIcon,
    TwitterIcon,
  },
  mixins: [
    Analytics,
    FeatureFlags,
  ],
  data() {
    return {
      links,
      FacebookIcon,
      InstagramIcon,
      LinkedInIcon,
      TwitterIcon,
      urlsToInclude: [
        {
          name: 'Support',
          url: 'support',
          analyticsEvent: 'support',
        },
        {
          name: 'Dealer documents',
          url: 'dealerForms',
          analyticsEvent: 'dealerForms',
        },
        {
          name: 'Legal',
          url: 'legal',
          analyticsEvent: 'legal',
        },
        {
          name: 'Fee Schedule',
          url: 'feeSchedule',
          analyticsEvent: 'feeSchedule',
        },
        {
          name: 'Terms of Service',
          url: 'legal',
          analyticsEvent: 'termsOfService',
        },
        {
          name: 'Privacy Policy / Notice of collection',
          url: 'privacy',
          analyticsEvent: 'privacyPolicy',
        },
      ],
      socialsToInclude: [
        {
          name: 'LinkedIn',
          url: links.social.linkedin,
          analyticsEvent: 'linkedin',
          title: 'Connect with us on LinkedIn',
        },
        {
          name: 'Instagram',
          url: links.social.instagram,
          analyticsEvent: 'instagram',
          title: 'Follow us on Instagram',
        },
        {
          name: 'Facebook',
          url: links.social.facebook,
          analyticsEvent: 'facebook',
          title: 'Like us on Facebook',
        },
        {
          name: 'Twitter',
          url: links.social.twitter,
          analyticsEvent: 'twitter',
          title: 'Follow us on Twitter',
        },
      ],
    };
  },
  computed: {
    getCopyrightYear() {
      const copyrightYear = new Date().getFullYear();
      return copyrightYear;
    },
    filteredUrls() {
      return this.urlsToInclude.filter((link) => {
        if (link.url === 'feeSchedule') {
          return this.featureFlags[this.featureFlagSlugs.enableNewFeeSchedulePage];
        }
        return true;
      });
    },
  },
  methods: {
    trackAppStoreBadgeClicked() {
      this.track(this.eventNames.appStoreBadgeClicked);
    },
    trackGooglePlayBadgeClicked() {
      this.track(this.eventNames.googlePlayStoreBadgeClicked);
    },
    trackSocialLinkClicked(siteName) {
      this.track(this.eventNames.footer[`${siteName}Clicked`]);
    },
    trackFooterLinkClicked(analyticsEvent) {
      this.track(this.eventNames.footer[`${analyticsEvent}LinkClicked`]);
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/scss/search/fonts';

.footer {
  @apply tw-bg-transparent
    tw-py-3
    tw-px-6;

  padding-top: 1.8rem;
  padding-bottom: 1.8rem;
  z-index: 2;
  border-top: 1px solid theme('colors.gray.dark');

  @screen tw-md {
    @apply tw-py-6
      tw-px-12;
  }

  @screen tw-sm {
    padding-top: 0;
  }

  @apply tw-bg-white;

  .footer-content {
    @apply tw-text-text-tertiary
      tw-justify-center;

    margin: 0 auto;
    text-align: center;

    .logo {
      display: block;
      width: 100px;
      margin: 15px auto 20px;

      @screen tw-md {
        margin: 30px auto 20px;
      }
    }

    .links-row {
      margin-bottom: 20px;
    }

    .acv-copyright {
      margin: 25px 0 30px;

      @screen tw-lg {
        margin: 25px 0 0;
      }
    }

    .app-store-badges {
      max-width: 240px;
      margin: 0 auto;
    }

    .app-store {
      display: inline-block;
      width: 50%;
      position: relative;

      svg {
        width: 102px;
        height: auto;
      }
    }

    .social-media-links {
      max-width: 171px;
      margin: 0 auto 20px;
    }

    .social-media {
      display: inline-block;
      width: 25%;
      position: relative;

      svg {
        width: 24px;
        height: auto;
      }
    }
  }

  a {
    @apply tw-text-text-tertiary;

    &:hover,
    &:active,
    &:focus {
      @apply tw-text-text-link-on-light;
    }

    &.footer-link {
      display: block;
      width: 100%;
      margin-top: 5px;

      @screen tw-md {
        display: inline;
        width: auto;
        margin-top: 0;
      }
    }
  }

  .footer-bullet {
    display: none;
    font-size: 0.75em;
    padding: 0 0.25rem;

    @screen tw-md {
      display: inline;
    }
  }
}
</style>
